import React, { useState } from 'react';

import { Tooltip } from 'antd';

import AiCiSectionIcon from 'components/AiCompetitiveIntelligence/AiCiSectionIcon';
import PillSwitcher from 'components/lib/PillSwitcher';
import { COMMUNICATION_CHANNEL_ORDER } from 'constants/communicationTags';
import { SpeedToLeadMetrics, SpeedToLeadSequenceEvent } from 'types/recommendations';

import { SPEED_TO_LEAD_FOLLOW_UP_DAYS, SPEED_TO_LEAD_FOLLOW_UP_VIEW } from './constants';
import {
  ChannelBar,
  ChannelLegend,
  ChannelSegment,
  FollowUpHeading,
  FollowUpRow,
  FollowUpTable,
  FollowUpTouches,
  LegendDot,
  LegendItem,
  SequenceDay,
  SequenceDayHeading,
  SequenceDot,
  SequenceHeading,
  SequenceRow,
  SequenceScroll,
} from './SpeedToLeadFollowUp.styles';
import SpeedToLeadFollowUpTooltip, { SPEED_TO_LEAD_FOLLOW_UP_TOOLTIP_CLASS_NAME } from './SpeedToLeadFollowUpTooltip';
import { getChannelLabel, getEventChannel } from './speedToLeadOverallUtils';
import {
  CommunityMeta,
  CommunityName,
  DashboardCard,
  SectionDescription,
  SectionHeading,
  SectionTitle,
} from './SpeedToLeadSection.styles';
import SpeedToLeadTouchpointPreviewModal from './SpeedToLeadTouchpointPreviewModal';
import { TouchpointPreviewSelection } from './SpeedToLeadTouchpointPreviewModal.types';
import { getTouchpointKey } from './touchpointUtils';

type FollowUpCommunity = SpeedToLeadMetrics['follow_up']['communities'][number] & {
  events: Array<SpeedToLeadSequenceEvent & { channel: string }>;
};

const FollowUpLegend = ({ channels }: { channels: string[] }) => (
  <ChannelLegend>
    {channels.map((channel) => (
      <LegendItem key={channel}>
        <LegendDot channel={channel} />
        {getChannelLabel(channel)}
      </LegendItem>
    ))}
  </ChannelLegend>
);

const CommunityDetails = ({
  community,
  primaryCommunityId,
}: {
  community: FollowUpCommunity;
  primaryCommunityId: number | null;
}) => (
  <div>
    <CommunityName>{community.community_name}</CommunityName>
    <CommunityMeta>{community.community_id === primaryCommunityId ? 'My Community' : 'Competitor'}</CommunityMeta>
  </div>
);

const ChannelFollowUp = ({ communities, channels, primaryCommunityId, onPreviewOpen }: FollowUpProps) => {
  const sortedCommunities = [...communities].sort((left, right) => right.events.length - left.events.length);
  const maximumTouches = Math.max(...sortedCommunities.map((community) => community.events.length), 1);

  return (
    <FollowUpTable>
      <FollowUpHeading>
        <div>Rank</div>
        <div>Community</div>
        <div />
        <FollowUpTouches>Touches</FollowUpTouches>
      </FollowUpHeading>
      {sortedCommunities.map((community, index) => {
        const channelCounts = community.events.reduce<Record<string, number>>((counts, event) => {
          counts[event.channel] = (counts[event.channel] ?? 0) + 1;
          return counts;
        }, {});
        const terminalChannel = [...channels].reverse().find((channel) => (channelCounts[channel] ?? 0) > 0);
        return (
          <FollowUpRow key={community.community_id} isPrimary={community.community_id === primaryCommunityId}>
            <div>{index + 1}</div>
            <CommunityDetails community={community} primaryCommunityId={primaryCommunityId} />
            <ChannelBar>
              {channels.map((channel) => {
                const channelTouches = channelCounts[channel] ?? 0;
                const segment = (
                  <ChannelSegment
                    type="button"
                    channel={channel}
                    isTerminal={channel === terminalChannel}
                    width={(channelTouches / maximumTouches) * 100}
                    disabled={channelTouches === 0}
                    onClick={() => {
                      if (channelTouches > 0) {
                        onPreviewOpen({
                          sourceCommunityId: community.community_id,
                          communityName: community.community_name,
                          isPrimary: community.community_id === primaryCommunityId,
                          mode: SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL,
                          channel,
                        });
                      }
                    }}
                    aria-label={`Preview ${getChannelLabel(channel)} touchpoints for ${community.community_name}`}
                    data-test-id={`speed-to-lead-channel-preview-${community.community_id}-${channel}`}
                  />
                );

                return channelTouches > 0 ? (
                  <Tooltip
                    key={channel}
                    arrow={false}
                    overlayClassName={SPEED_TO_LEAD_FOLLOW_UP_TOOLTIP_CLASS_NAME}
                    title={
                      <SpeedToLeadFollowUpTooltip
                        channelLabel={getChannelLabel(channel)}
                        channelTouches={channelTouches}
                        communityName={community.community_name}
                        totalTouches={community.events.length}
                      />
                    }
                  >
                    {segment}
                  </Tooltip>
                ) : (
                  React.cloneElement(segment, { key: channel })
                );
              })}
            </ChannelBar>
            <FollowUpTouches>{community.events.length}</FollowUpTouches>
          </FollowUpRow>
        );
      })}
      <FollowUpLegend channels={channels} />
    </FollowUpTable>
  );
};

type FollowUpProps = {
  channels: string[];
  communities: FollowUpCommunity[];
  primaryCommunityId: number | null;
  onPreviewOpen: (selection: TouchpointPreviewSelection) => void;
};

const SequenceFollowUp = ({ communities, channels, primaryCommunityId, onPreviewOpen }: FollowUpProps) => {
  const sortedCommunities = [...communities].sort((left, right) => right.events.length - left.events.length);
  return (
    <SequenceScroll>
      <FollowUpTable>
        <SequenceHeading>
          <div>Rank</div>
          <div>Community</div>
          {SPEED_TO_LEAD_FOLLOW_UP_DAYS.map((day) => (
            <SequenceDayHeading key={day}>D{day}</SequenceDayHeading>
          ))}
          <FollowUpTouches>Touches</FollowUpTouches>
        </SequenceHeading>
        {sortedCommunities.map((community, index) => (
          <SequenceRow key={community.community_id} isPrimary={community.community_id === primaryCommunityId}>
            <div>{index + 1}</div>
            <CommunityDetails community={community} primaryCommunityId={primaryCommunityId} />
            {SPEED_TO_LEAD_FOLLOW_UP_DAYS.map((day) => (
              <SequenceDay key={day}>
                {community.events
                  .filter((event) => event.day === day)
                  .map((event) => (
                    <Tooltip
                      key={getTouchpointKey(event, community.events)}
                      arrow={false}
                      overlayClassName={SPEED_TO_LEAD_FOLLOW_UP_TOOLTIP_CLASS_NAME}
                      title={
                        <SpeedToLeadFollowUpTooltip
                          channelLabel={getChannelLabel(event.channel)}
                          communityName={community.community_name}
                          day={day}
                        />
                      }
                    >
                      <SequenceDot
                        type="button"
                        channel={event.channel}
                        onClick={() =>
                          onPreviewOpen({
                            sourceCommunityId: community.community_id,
                            communityName: community.community_name,
                            isPrimary: community.community_id === primaryCommunityId,
                            mode: SPEED_TO_LEAD_FOLLOW_UP_VIEW.SEQUENCE,
                            day,
                            activityId: event.activity_id,
                          })
                        }
                        aria-label={`Preview day ${day} touchpoints for ${community.community_name}`}
                        data-test-id={`speed-to-lead-sequence-preview-${community.community_id}-${getTouchpointKey(event, community.events)}`}
                      />
                    </Tooltip>
                  ))}
              </SequenceDay>
            ))}
            <FollowUpTouches>{community.events.length}</FollowUpTouches>
          </SequenceRow>
        ))}
        <FollowUpLegend channels={channels} />
      </FollowUpTable>
    </SequenceScroll>
  );
};

type Props = {
  analysisRunId: number | null;
  companyId: number;
  communities: SpeedToLeadMetrics['follow_up']['communities'];
  followUpView: string;
  onFollowUpViewChange: (view: string) => void;
  primaryCommunityId: number | null;
};

const SpeedToLeadFollowUp = ({
  analysisRunId,
  companyId,
  communities,
  followUpView,
  onFollowUpViewChange,
  primaryCommunityId,
}: Props) => {
  const [previewSelection, setPreviewSelection] = useState<TouchpointPreviewSelection | null>(null);
  const channelCommunities = communities.map((community) => ({
    ...community,
    events: community.sequence.map((event) => ({ ...event, channel: getEventChannel(event) })),
  }));
  const channelKeys = Array.from(
    new Set(channelCommunities.flatMap((community) => community.events.map((event) => event.channel)))
  ).sort((left, right) => {
    const leftIndex = COMMUNICATION_CHANNEL_ORDER.indexOf(left);
    const rightIndex = COMMUNICATION_CHANNEL_ORDER.indexOf(right);
    return (
      (leftIndex < 0 ? COMMUNICATION_CHANNEL_ORDER.length : leftIndex) -
      (rightIndex < 0 ? COMMUNICATION_CHANNEL_ORDER.length : rightIndex)
    );
  });
  const followUpProps = {
    communities: channelCommunities,
    channels: channelKeys,
    primaryCommunityId,
    onPreviewOpen: setPreviewSelection,
  };

  return (
    <DashboardCard $muted>
      <SectionHeading>
        <AiCiSectionIcon src="/icons/calendar-month.svg" />
        <div>
          <SectionTitle>7-Day Follow-Up Persistence</SectionTitle>
          <SectionDescription>
            Total touches across email, phone calls, phone calls that also leave voicemail, and SMS, within 7 days of
            inquiry.
          </SectionDescription>
        </div>
      </SectionHeading>
      <PillSwitcher
        items={[
          { key: SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL, label: 'Channel' },
          { key: SPEED_TO_LEAD_FOLLOW_UP_VIEW.SEQUENCE, label: 'Sequence' },
        ]}
        activeKey={followUpView}
        onChange={onFollowUpViewChange}
      />
      {followUpView === SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL ? (
        <ChannelFollowUp {...followUpProps} />
      ) : (
        <SequenceFollowUp {...followUpProps} />
      )}
      {previewSelection && primaryCommunityId && analysisRunId && (
        <SpeedToLeadTouchpointPreviewModal
          analysisRunId={analysisRunId}
          companyId={companyId}
          primaryCommunityId={primaryCommunityId}
          selection={previewSelection}
          onClose={() => setPreviewSelection(null)}
        />
      )}
    </DashboardCard>
  );
};

export default SpeedToLeadFollowUp;
